import { uiText, useUiLanguage } from '../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ref, get, set, update, push } from 'firebase/database';
import { database } from '../firebase';
import { decryptData } from '../utils/crypto';

// Decode slug using AES first, then Base64 fallback
const decodeSlug = (raw) => {
  const aes = decryptData(raw);
  if (aes) return aes;
  try { return atob(raw); } catch (e) { }
  try { const p = raw.replace(/-/g, '+').replace(/_/g, '/'); return atob(p); } catch (e) { }
  return null;
};

const ContactInfoCollector = () => {
  useUiLanguage();
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState('');
  const [applicantId, setApplicantId] = useState('');
  const [connectionKey, setConnectionKey] = useState('');
  const [employeeKey, setEmployeeKey] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Extended fields
  const [dob, setDob] = useState('');
  const [nationalId, setNationalId] = useState('');
  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [city, setCity] = useState('');
  const [region, setRegion] = useState(''); // province / region / state (free text)
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [emg1Name, setEmg1Name] = useState('');
  const [emg1Relation, setEmg1Relation] = useState('');
  const [emg1Phone, setEmg1Phone] = useState('');
  const [emg1Email, setEmg1Email] = useState('');
  const [emg2Name, setEmg2Name] = useState('');
  const [emg2Relation, setEmg2Relation] = useState('');
  const [emg2Phone, setEmg2Phone] = useState('');
  const [emg2Email, setEmg2Email] = useState('');

  useEffect(() => {
    const init = async () => {
      try {
        const decoded = decodeSlug(slug);
        if (!decoded) throw new Error('Invalid link');
        const parts = decoded.split('_');
        if (parts.length < 3) throw new Error('Invalid link');
        const cid = parts[0];
        const token = parts[1];
        const aid = parts.slice(2).join('_');
        if (token !== 'contact') throw new Error('Invalid token');
        setCompanyId(cid);
        setApplicantId(aid);

        // Determine connection key (authenticated vs imported)
        let key = `${cid}_${aid}`;
        let employeeK = '';
        const authSnap = await get(ref(database, `connections/${key}`));
        if (!authSnap.exists()) {
          const impKey = `${cid}_imported_${aid}`;
          const impSnap = await get(ref(database, `connections/${impKey}`));
          if (!impSnap.exists()) throw new Error('Connection not found');
          key = impKey;
          employeeK = impSnap.val()?.employeeKey || '';
        } else {
          employeeK = authSnap.val()?.employeeKey || '';
        }
        setConnectionKey(key);
        setEmployeeKey(employeeK);

        // Prefill from profiles if available
        if (aid.startsWith('temp_')) {
          // Try NEW path first
          let profSnap = await get(ref(database, `imported_profiles/${cid}/${aid}`));
          if (!profSnap.exists()) {
            profSnap = await get(ref(database, `imported_profiles/${aid}`));
          }
          const prof = profSnap.val() || {};
          setName(prof.name || '');
          setEmail(prof.email || '');
        } else {
          const personSnap = await get(ref(database, `people/${aid}`));
          const person = personSnap.val() || {};
          setName(person.username || person.name || '');
        }
      } catch (e) {
        console.error(e);
        setError(uiText('Invalid or expired link.'));
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [slug]);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!connectionKey) throw new Error('Missing connection');
      // Build extended contact info object (keep legacy keys for compatibility)
      const contactInfo = {
        // legacy
        email: email || '',
        phone: phone || '',
        // new structured
        personalEmail: email || '',
        personalPhone: phone || '',
        address: {
          line1: addrLine1 || '',
          line2: addrLine2 || '',
          city: city || '',
          region: region || '',
          postalCode: postalCode || '',
          country: country || ''
        },
        emergencyContacts: [
          ...(emg1Name || emg1Phone || emg1Email ? [{ name: emg1Name || '', relationship: emg1Relation || '', phone: emg1Phone || '', email: emg1Email || '' }] : []),
          ...(emg2Name || emg2Phone || emg2Email ? [{ name: emg2Name || '', relationship: emg2Relation || '', phone: emg2Phone || '', email: emg2Email || '' }] : [])
        ],
        dateOfBirth: dob || '',
        nationalId: nationalId || '',
        updatedAt: Date.now()
      };
      // Save to connection (applicant data)
      await update(ref(database, `connections/${connectionKey}`), { contactInfo });
      // Save to employee if we have the key; otherwise create one and link it
      if (employeeKey) {
        await update(ref(database, `companies/${companyId}/employees/${employeeKey}/contact`), {
          // workEmail intentionally not set here; handled by admin in MyTeam
          personalEmail: email || '',
          personalPhone: phone || '',
          email: email || '', // backward compatibility
          phone: phone || '', // backward compatibility
          address: contactInfo.address,
          emergencyContacts: contactInfo.emergencyContacts,
          dateOfBirth: contactInfo.dateOfBirth,
          nationalId: contactInfo.nationalId
        });
        await update(ref(database, `companies/${companyId}/employees/${employeeKey}`), {
          'meta/updatedAt': Date.now()
        });
      } else {
        // Create a minimal employee record with this contact info and link it back
        const baseRef = ref(database, `companies/${companyId}/employees`);
        const newEmpRef = push(baseRef);
        await set(newEmpRef, {
          name: name || '',
          contact: {
            personalEmail: email || '',
            personalPhone: phone || '',
            email: email || '', // legacy
            phone: phone || '', // legacy
            address: contactInfo.address,
            emergencyContacts: contactInfo.emergencyContacts,
            dateOfBirth: contactInfo.dateOfBirth,
            nationalId: contactInfo.nationalId
          },
          source: 'contact-info',
          externalApplicantId: applicantId,
          createdAt: Date.now(),
          status: 'pre-onboarding'
        });
        await update(ref(database, `connections/${connectionKey}`), {
          employeeKey: newEmpRef.key
        });
      }
      // Also store a denormalized contact section for imported/applicant profiles if permitted
      try {
        if (applicantId.startsWith('temp_')) {
          // Try NEW path first, fallback to legacy
          const newPath = `imported_profiles/${companyId}/${applicantId}/contact`;
          await update(ref(database, newPath), {
            personalEmail: email || '',
            personalPhone: phone || '',
            address: contactInfo.address,
            emergencyContacts: contactInfo.emergencyContacts,
            dateOfBirth: contactInfo.dateOfBirth,
            nationalId: contactInfo.nationalId,
            email: email || '', // legacy
            phone: phone || ''  // legacy
          });
        } else {
          // Some environments restrict writes to /people via client; ignore errors silently
          await update(ref(database, `people/${applicantId}/contact`), {
            personalEmail: email || '',
            personalPhone: phone || '',
            address: contactInfo.address,
            emergencyContacts: contactInfo.emergencyContacts,
            dateOfBirth: contactInfo.dateOfBirth,
            nationalId: contactInfo.nationalId,
            email: email || '', // legacy
            phone: phone || ''  // legacy
          });
        }
      } catch (ignored) { }
      setSubmitted(true);
    } catch (err) {
      console.error('Submit error:', err);
      setError(uiText('Failed to submit. Please try again later.'));
    }
  };

  if (loading) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f6f8fb', fontFamily: 'Montserrat, sans-serif'
    }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', border: '5px solid #e1e8f5',
          borderTopColor: '#568ded', animation: 'spin 1s linear infinite'
        }} />
        <style>{'@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }'}</style>
        <div style={{ marginTop: 14, color: '#568ded', fontSize: 16 }}>{uiText("Loading…")}</div>
      </div>
    </div>
  );
  if (submitted) return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#f6f8fb', fontFamily: 'Montserrat, sans-serif'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: 80, height: 80, borderRadius: '50%', background: '#e9f7ef',
          margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: '2px solid #2ecc71'
        }}>
          <span style={{ color: '#2ecc71', fontSize: 40 }}>✓</span>
        </div>
        <h2 style={{ margin: 0, marginBottom: 8 }}>{uiText("Thank you!")}</h2>
        <p style={{ marginTop: 0, color: '#555' }}>{uiText("Your contact information has been submitted successfully.")}</p>
        <p style={{ marginTop: 8, color: '#777' }}>{uiText("You may close this tab now.")}</p>
      </div>
    </div>
  );
  if (error) return <div style={{ padding: 20, color: '#c0392b' }}>{uiText(error)}</div>;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f6f8fb',
      fontFamily: 'Montserrat, sans-serif'
    }}>
      <form onSubmit={onSubmit} style={{
        width: '100%',
        maxWidth: 520,
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(3,17,40,0.12)',
        padding: 24
      }}>
        <h2 style={{ marginTop: 0, marginBottom: 8 }}>{uiText("Contact Information")}</h2>
        <p style={{ marginTop: 0, color: '#555' }}>{uiText("Please confirm your contact details for pre-onboarding.")}</p>
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Full Name")}</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Personal Email")}</label>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Personal Phone")}</label>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>

        <h3 style={{ marginTop: 18, marginBottom: 8, fontSize: 18, color: "var(--app-neutral-ink, #031128)" }}>{uiText("Address")}</h3>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Address Line 1")}</label>
          <input type="text" value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Address Line 2 (optional)")}</label>
          <input type="text" value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="modal-row" style={{ gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("City")}</label>
            <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Region / State / Province")}</label>
            <input type="text" value={region} onChange={(e) => setRegion(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
        </div>
        <div className="modal-row" style={{ gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Postal Code")}</label>
            <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Country")}</label>
            <input type="text" value={country} onChange={(e) => setCountry(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
        </div>

        <h3 style={{ marginTop: 18, marginBottom: 8, fontSize: 18, color: "var(--app-neutral-ink, #031128)" }}>{uiText("Emergency Contacts")}</h3>
        <div className="form-group" style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Primary Contact - Full Name")}</label>
          <input type="text" value={emg1Name} onChange={(e) => setEmg1Name(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="modal-row" style={{ gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Relationship")}</label>
            <input type="text" value={emg1Relation} onChange={(e) => setEmg1Relation(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Phone")}</label>
            <input type="tel" value={emg1Phone} onChange={(e) => setEmg1Phone(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Email (optional)")}</label>
          <input type="email" value={emg1Email} onChange={(e) => setEmg1Email(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>

        <div className="form-group" style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Secondary Contact - Full Name (optional)")}</label>
          <input type="text" value={emg2Name} onChange={(e) => setEmg2Name(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>
        <div className="modal-row" style={{ gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Relationship")}</label>
            <input type="text" value={emg2Relation} onChange={(e) => setEmg2Relation(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Phone")}</label>
            <input type="tel" value={emg2Phone} onChange={(e) => setEmg2Phone(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
        </div>
        <div className="form-group" style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Email (optional)")}</label>
          <input type="email" value={emg2Email} onChange={(e) => setEmg2Email(e.target.value)} className="text-input" style={{ width: '100%' }} />
        </div>

        <h3 style={{ marginTop: 18, marginBottom: 8, fontSize: 18, color: "var(--app-neutral-ink, #031128)" }}>{uiText("Additional")}</h3>
        <div className="modal-row" style={{ gap: 12 }}>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("Date of Birth")}</label>
            <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
          <div className="form-group" style={{ flex: 1 }}>
            <label style={{ display: 'block', marginBottom: 6 }}>{uiText("National ID / Passport Number (optional)")}</label>
            <input type="text" value={nationalId} onChange={(e) => setNationalId(e.target.value)} className="text-input" style={{ width: '100%' }} />
          </div>
        </div>
        <button type="submit" className="btn-primary" style={{ width: '100%' }}>{uiText("Submit")}</button>
      </form>
    </div>
  );
};

export default ContactInfoCollector;
